import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Compass, Sparkles } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import { Reveal } from "../lib/motion";
import { TOPICS } from "../data/topics";

export default function WhereShouldIStart() {
    return (
        <div
            className="min-h-screen bg-page text-ink"
            data-testid="where-should-i-start-page"
        >
            <Nav />
            <main>
                {/* Hero */}
                <section className="relative overflow-hidden border-b border-hairline py-20 lg:py-28">
                    <div className="pointer-events-none absolute -top-16 -right-16 h-[480px] w-[480px] animate-blob rounded-full bg-coral/20 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-24 left-1/3 h-[360px] w-[360px] animate-blob rounded-full bg-sun/15 blur-3xl" />

                    <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10">
                        <Link
                            to="/"
                            className="mb-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-ink/60 hover:text-coral"
                            data-testid="topics-back-home"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to home
                        </Link>

                        <Reveal>
                            <div className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-coral">
                                <Compass className="h-4 w-4" />
                                The playbook
                            </div>
                            <h1
                                className="font-display text-5xl font-black leading-[0.95] tracking-tight text-ink sm:text-6xl lg:text-[6rem]"
                                data-testid="topics-page-headline"
                            >
                                Where should I{" "}
                                <span className="font-serif italic font-normal text-coral">
                                    start?
                                </span>
                            </h1>
                            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/75 lg:text-lg">
                                A ten-part playbook for small business owners. Every guide answers{" "}
                                <strong className="text-ink">one</strong> question in under five
                                minutes and finishes with one thing you can do today. Read it,
                                see it as a flowchart, or tick it off as a checklist — whichever
                                way works for you.
                            </p>
                        </Reveal>
                    </div>
                </section>

                {/* Topics grid */}
                <section
                    className="border-b border-hairline bg-white py-16 lg:py-24"
                    data-testid="topics-grid-section"
                >
                    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
                            <div>
                                <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                                    // The topics
                                </div>
                                <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-ink lg:text-5xl">
                                    Start anywhere.
                                </h2>
                            </div>
                            <div className="max-w-md text-sm leading-relaxed text-ink/60">
                                We&apos;re writing these as fast as our small crew can. Pop back
                                soon — or grab the free 7-day checklist below to get started
                                today.
                            </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {TOPICS.map((t, i) => (
                                <Link
                                    key={t.slug}
                                    to={`/where-should-i-start/${t.slug}`}
                                    className="group flex h-full flex-col justify-between border-2 border-ink/15 bg-page p-6 transition-all hover:-translate-y-1 hover:border-coral hover:frame-coral lg:p-7"
                                    data-testid={`topic-tile-${t.slug}`}
                                >
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <span className="font-display text-xl font-black text-coral">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-ink/40">
                                                5-min read
                                            </span>
                                        </div>
                                        <h3
                                            className="mt-6 font-display text-2xl font-black leading-[1.05] tracking-tight text-ink lg:text-3xl"
                                            data-testid={`topic-tile-question-${t.slug}`}
                                        >
                                            {t.question}
                                        </h3>
                                    </div>
                                    <div className="mt-8 flex items-center justify-between gap-2 border-t border-ink/10 pt-4">
                                        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-ink/50 group-hover:text-coral">
                                            Read the guide
                                        </span>
                                        <ArrowUpRight className="h-5 w-5 text-ink/50 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-coral" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA — nudge to 7-day checklist while guides are being written */}
                <section
                    className="border-b border-hairline bg-page py-16 lg:py-24"
                    data-testid="topics-cta-section"
                >
                    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                        <div className="grid gap-8 border-2 border-coral bg-coral p-8 text-ink lg:grid-cols-12 lg:items-center lg:gap-12 lg:p-12">
                            <div className="lg:col-span-8">
                                <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-ink/70">
                                    <Sparkles className="h-4 w-4" />
                                    While you wait
                                </div>
                                <h3 className="font-display text-3xl font-black leading-[0.95] tracking-tight lg:text-5xl">
                                    Grab the free 7-day get-found-online checklist.
                                </h3>
                                <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink/80 lg:text-base">
                                    A one-page PDF you can print, stick on the fridge, and tick off
                                    over a week. No email spam — just the checklist and one gentle
                                    follow-up.
                                </p>
                            </div>
                            <div className="lg:col-span-4 lg:text-right">
                                <Link
                                    to="/#playbook"
                                    className="group inline-flex items-center gap-3 border-2 border-ink bg-page px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-1 hover:frame"
                                    data-testid="topics-cta-checklist"
                                >
                                    Get the checklist
                                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
